/**
 * Web POS local persistence: catalog cache, held bills, offline sale queue.
 * Scoped by clientId when available.
 */

const PRODUCTS = 'pos_cache_products_v1';
const CUSTOMERS = 'pos_cache_customers_v1';
const HOLDS = 'pos_held_bills_v1';
const QUEUE = 'pos_offline_queue_v1';
const LAST_CLIENT = 'pos_last_client_id';

function key(base, clientId) {
  return clientId ? `${base}:${clientId}` : base;
}

function readJson(k, fallback) {
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(k, value) {
  try {
    localStorage.setItem(k, JSON.stringify(value));
  } catch (e) {
    console.warn('posLocalStore write failed:', e);
  }
}

function readCatalogFor(clientId) {
  const products = readJson(key(PRODUCTS, clientId), null);
  const customers = readJson(key(CUSTOMERS, clientId), null);
  if (!products && !customers) return null;
  return {
    products: Array.isArray(products) ? products : [],
    customers: Array.isArray(customers) ? customers : []
  };
}

export function cacheCatalog(clientId, products, customers) {
  const id = clientId || 'default';
  writeJson(key(PRODUCTS, id), products || []);
  writeJson(key(CUSTOMERS, id), customers || []);
  try {
    localStorage.setItem(LAST_CLIENT, id);
  } catch (_) {
    /* ignore */
  }
}

export function loadCatalogCache(clientId) {
  const id = clientId || 'default';
  let cached = readCatalogFor(id);
  if (cached && (cached.products.length || cached.customers.length)) return cached;

  try {
    const last = localStorage.getItem(LAST_CLIENT);
    if (last && last !== id) {
      cached = readCatalogFor(last);
      if (cached && (cached.products.length || cached.customers.length)) return cached;
    }
  } catch (_) {
    /* ignore */
  }

  if (id !== 'default') {
    cached = readCatalogFor('default');
    if (cached && (cached.products.length || cached.customers.length)) return cached;
  }
  return null;
}

export function loadHolds(clientId) {
  const list = readJson(key(HOLDS, clientId || 'default'), []);
  return Array.isArray(list) ? list : [];
}

export function saveHolds(clientId, holds) {
  writeJson(key(HOLDS, clientId || 'default'), Array.isArray(holds) ? holds : []);
}

export function loadQueue(clientId) {
  const list = readJson(key(QUEUE, clientId || 'default'), []);
  return Array.isArray(list) ? list : [];
}

export function saveQueue(clientId, queue) {
  writeJson(key(QUEUE, clientId || 'default'), Array.isArray(queue) ? queue : []);
}

export function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** True when the failure looks like connectivity / server unreachable (safe to queue sale). */
export function isQueueableError(error) {
  if (!error) return true;
  if (!error.response) return true;
  const status = error.response.status;
  if (!status || status === 0 || status === 408 || status === 429) return true;
  if (status >= 500) return true;
  const msg = `${error.message || ''} ${error.code || ''}`.toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('timeout') ||
    msg.includes('failed to fetch') ||
    msg.includes('err_network') ||
    msg.includes('econnaborted')
  );
}

/**
 * Probe API reachability. navigator.onLine alone is unreliable on Windows.
 */
export async function canReachApi(timeoutMs = 2500) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch('/api/health', {
      method: 'GET',
      cache: 'no-store',
      signal: ctrl.signal,
      headers: { Accept: 'application/json' }
    });
    clearTimeout(timer);
    return Boolean(res && res.ok);
  } catch {
    return false;
  }
}
